import { useTranslation } from "react-i18next";
import {ScissorOutlined, DeleteOutlined, CloseOutlined, CheckOutlined} from "@ant-design/icons";
import {Select, Spin, Modal, Col, Form, Row, Space, Button, Input, DatePicker} from "antd";
import moment from "moment";
import {useState, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {CaretakerCreateRequestDto, CaretakerUpdateRequestDto} from "../api/client-axios";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {RootState} from "../app/store";
import {Problem} from "../model/problem";
import {deleteNoOrgCaretaker, fetchNoOrgCaretakers} from "../features/caretakersNoOrgSlice";
import {
    clearSaveNoOrgCaretakerError,
    fetchNoOrgCaretaker,
    initNoOrgCaretakerState, saveNoOrgCaretaker,
    updateNoOrgCaretakerFields, updateNoOrgCaretakerPhoneNumber
} from "../features/caretakerNoOrgSlice";

const {Option} = Select;

export const EditNoOrgCaretakerPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const caretakersStatus = useAppSelector((state: RootState) => state.caretakersNoOrg.status);
    const caretaker = useAppSelector((state: RootState) => state.caretakerNoOrg.caretaker);
    const status = useAppSelector((state: RootState) => state.caretakerNoOrg.status);
    const error = useAppSelector((state: RootState) => state.caretakerNoOrg.error);
    const countries = useAppSelector((state: RootState) => state.country.countries);


    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isRemoveCaretakerWarningVisible, setIsRemoveCaretakerWarningVisible] = useState(false);
    const [isDeleteCaretakerWarningVisible, setIsDeleteCaretakerWarningVisible] = useState(false);

    const caretakerId = params.caretakerId;

    useEffect(() => {
        if (caretakersStatus === "idle") {
            dispatch(fetchNoOrgCaretakers({}));
        }
        dispatch(fetchNoOrgCaretaker({caretakerId: caretakerId!}));
    }, []);

    useEffect(() => {
        if (status === "saved") {
            dispatch(fetchNoOrgCaretakers({}));
            dispatch(initNoOrgCaretakerState());
            navigate(-1);
        }
        if (status === "auth-error" || status === "error") {
            setIsModalVisible(true);
        }
    }, [status])


    const onCancel = () => {
        navigate(-1);
    }

    const changeCaretaker = (caretaker: CaretakerCreateRequestDto) => {
        dispatch(updateNoOrgCaretakerFields(caretaker));
    }

    const onSave = () => {
        dispatch(saveNoOrgCaretaker({caretakerId: caretakerId!, caretaker: caretaker as CaretakerUpdateRequestDto}));
    }

    const onDelete = () => {
        setIsDeleteCaretakerWarningVisible(true);
    }

    const onRemove = () => {
        setIsRemoveCaretakerWarningVisible(true);
    }

    const onDeleteCaretaker = () => {
        if (caretaker?.id != null) {
            dispatch(deleteNoOrgCaretaker({caretakerId: caretaker.id}));
        }
    }

    const onConfirm = (phoneNumber: string) => {
        dispatch(updateNoOrgCaretakerPhoneNumber(phoneNumber));
    }

    const handleAuthOk = () => {
        setIsModalVisible(false)
        navigate("/")
    }

    const handleErrorOk = () => {
        setIsModalVisible(false);
        dispatch(clearSaveNoOrgCaretakerError());
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }


    if (status === "idle" || status === "loading" || caretakersStatus === "idle") {
        return <Spin/>;
    }

    if (status === "auth-error") {
        return <Modal title={t("error.auth")} visible={isModalVisible} onOk={handleAuthOk} onCancel={handleCancel}>
            <p>{t("error.authFailedLogin")}</p>
        </Modal>
    }

    if (status === "error") {
        const apiError = error as Problem;
        const title = apiError.title ?? t("error.server");
        const detail = apiError.detail ?? error?.toString;
        return <Modal title={title} visible={isModalVisible} onOk={handleErrorOk} onCancel={handleCancel}>
            <p>{detail}</p>
        </Modal>
    }

    if (status === "saved") {
        return (<div></div>);
    }

    return (<div className='edit-person-main-frame'>
        <Col span={24}>
            <Form className='person-form'
                  name="basic"
                  labelCol={{span: 6}}
                  wrapperCol={{span: 24}}
                  initialValues={{remember: true}}
                //onFinish={onFinish}
                //onFinishFailed={onFinishFailed}
                  autoComplete="off"
            >

                <Row>
                    {/* <Col className='person-title' offset={6} span={11}>Caretaker</Col> */}
                    <Col offset={6} span={18}>
                        <Form.Item>
                            <Row justify="end">
                                <Space className="mr-xxs">
                                    <Button icon={<ScissorOutlined/>} onClick={onRemove} className="btn-warning"
                                            disabled={caretaker?.id === undefined}>{t("actions.remove")}</Button>
                                    <Button icon={<DeleteOutlined/>} onClick={onDelete} type='primary' danger
                                            disabled={caretaker?.id === undefined}>{t("actions.delete")}</Button>
                                </Space>

                                <Space>
                                    <Button icon={<CloseOutlined/>} onClick={onCancel}>{t("actions.cancel")}</Button>
                                    <Button icon={<CheckOutlined/>} type='primary' onClick={onSave}>{t("actions.save")}</Button>
                                </Space>
                            </Row>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.phoneNumber")} rules={[{required: true}]}
                                   initialValue={caretaker?.userAccount?.mobileNumber}>
                            <Input disabled placeholder={t("placeholder.phoneNumber")}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.email")}>
                            <Input placeholder={t("placeholder.email")}
                                   defaultValue={caretaker?.userAccount?.emailAddress || ""} onChange={(e) => {
                                changeCaretaker({...caretaker, email: e.target.value} as CaretakerCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.firstName")} rules={[{required: true, message: t("validation.firstNameRequired")}]}>
                            <Input placeholder={t("placeholder.firstName")} defaultValue={caretaker?.firstName || ""} onChange={(e) => {
                                changeCaretaker({...caretaker, firstName: e.target.value} as CaretakerCreateRequestDto)
                            }}/>
                        </Form.Item>

                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.lastName")}>
                            <Input placeholder={t("placeholder.lastName")} defaultValue={caretaker?.lastName || ""} onChange={(e) => {
                                changeCaretaker({...caretaker, lastName: e.target.value} as CaretakerCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.gender")}>
                            <Select defaultValue={caretaker?.gender} onChange={(e) => {
                                changeCaretaker({...caretaker, gender: e} as CaretakerCreateRequestDto)
                            }} style={{width: 100}}>
                                <Option value="Male">{t("gender.male")}</Option>
                                <Option value="Female">{t("gender.female")}</Option>
                                <Option value="Other">{t("gender.other")}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.birthday")}>
                            <DatePicker
                                defaultValue={caretaker?.birthday != undefined ? moment(caretaker.birthday, "YYYY-MM-DD").utc() : moment().utc()}
                                onChange={(e) => {
                                    changeCaretaker({
                                        ...caretaker,
                                        birthday: e?.format("")
                                    } as CaretakerCreateRequestDto)
                                }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.country")} initialValue={caretaker?.address?.country}>
                            <Select
                                onChange={(e) => changeCaretaker({
                                    ...caretaker,
                                    address: {...caretaker?.address, alpha3Code: e}
                                } as CaretakerCreateRequestDto)}
                                defaultValue={caretaker?.address?.alpha3Code}
                                showSearch
                                style={{width: 200}}
                                placeholder={t("placeholder.searchToSelect")}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    return (option!.children![2] as unknown as string).toLowerCase().includes(input.toLowerCase())
                                }}
                                filterSort={(optionA, optionB) =>
                                    (optionA!.children![2] as unknown as string)
                                        .toLowerCase()
                                        .localeCompare((optionB!.children![2] as unknown as string).toLowerCase())
                                }
                            >
                                {countries.map(country => <Option key={country.alpha3Code}
                                                                  value={country.alpha3Code}><img src={country.flag!}
                                                                                                  width="20"></img> {country.name}
                                </Option>)}

                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.address")}>
                            <Input defaultValue={caretaker?.address?.streetAddress1 as string | undefined}
                                   placeholder={t("placeholder.addressLine1")} onChange={(e) => {
                                changeCaretaker({
                                    ...caretaker,
                                    address: {...caretaker?.address, streetAddress1: e.target.value}
                                } as CaretakerCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.address")}>
                            <Input defaultValue={caretaker?.address?.streetAddress2 as string | undefined}
                                   placeholder={t("placeholder.addressLine2")} onChange={(e) => {
                                changeCaretaker({
                                    ...caretaker,
                                    address: {...caretaker?.address, streetAddress2: e.target.value}
                                } as CaretakerCreateRequestDto)
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.city")}>
                            <Input defaultValue={caretaker?.address?.city as string | undefined} placeholder={t("placeholder.city")}
                                   onChange={(e) => {
                                       changeCaretaker({
                                           ...caretaker,
                                           address: {...caretaker?.address, city: e.target.value}
                                       } as CaretakerCreateRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.state")}>
                            <Input defaultValue={caretaker?.address?.state as string | undefined} placeholder={t("placeholder.state")}
                                   onChange={(e) => {
                                       changeCaretaker({
                                           ...caretaker,
                                           address: {...caretaker?.address, state: e.target.value}
                                       } as CaretakerCreateRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={t("field.postcode")}>
                            <Input defaultValue={caretaker?.address?.postCode as string | undefined} placeholder={t("placeholder.postcode")}
                                   onChange={(e) => {
                                       changeCaretaker({
                                           ...caretaker,
                                           address: {...caretaker?.address, postCode: e.target.value}
                                       } as CaretakerCreateRequestDto)
                                   }}/>
                        </Form.Item>
                    </Col>


                </Row>
            </Form>
        </Col>
        <Modal visible={isDeleteCaretakerWarningVisible} okText={t("actions.yes")} cancelText={t("actions.no")}
               onCancel={() => setIsDeleteCaretakerWarningVisible(false)} onOk={onDeleteCaretaker}>{t("confirm.delete.caretaker", { name: [caretaker?.firstName, caretaker?.lastName].filter(Boolean).join(" ") })}</Modal>
    </div>);
}